import { useState } from 'react';
import ColorPicker, { FLEET_COLORS } from './ColorPicker';

function generateId(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
}

export default function FleetEditor({ fleets, onChange }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', color: FLEET_COLORS[0], default_start_time: '14:00' });

  const addFleet = () => {
    if (!draft.name.trim()) return;
    const id = generateId(draft.name);
    if (fleets.some((f) => f.id === id)) return;
    onChange([...fleets, { id, ...draft, name: draft.name.trim() }]);
    // Seed the next draft from the post-add palette position. Using fleets.length
    // here would reuse the just-added fleet's colour (stale pre-add length), making
    // the first two fleets default to the same blue.
    setDraft({ name: '', color: FLEET_COLORS[(fleets.length + 1) % FLEET_COLORS.length], default_start_time: '14:00' });
    setAdding(false);
  };

  const removeFleet = (id) => {
    onChange(fleets.filter((f) => f.id !== id));
  };

  const updateFleet = (id, field, value) => {
    onChange(fleets.map((f) => (f.id === id ? { ...f, [field]: value } : f)));
  };

  return (
    <section className="config-section">
      <h2>Fleets</h2>
      <p className="section-hint">
        Define the fleets your club uses (e.g. Blue, Red, Green). Each fleet can have a default warning signal time.
      </p>

      {fleets.length === 0 && !adding && (
        <p className="empty-state">No fleets defined yet.</p>
      )}

      {fleets.length > 0 && (
        <table className="config-table">
          <thead>
            <tr>
              <th>Colour</th>
              <th>Name</th>
              <th>ID</th>
              <th>Default Start</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {fleets.map((fleet) => (
              <tr key={fleet.id}>
                <td>
                  <ColorPicker
                    value={fleet.color || '#0066cc'}
                    onChange={(color) => updateFleet(fleet.id, 'color', color)}
                    label={`${fleet.name || 'fleet'} colour`}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={fleet.name}
                    onChange={(e) => updateFleet(fleet.id, 'name', e.target.value)}
                    className="inline-input"
                  />
                </td>
                <td><code>{fleet.id}</code></td>
                <td>
                  <input
                    type="time"
                    value={fleet.default_start_time || ''}
                    onChange={(e) => updateFleet(fleet.id, 'default_start_time', e.target.value)}
                    className="inline-input time-input"
                  />
                </td>
                <td>
                  <button className="btn btn-danger btn-sm" onClick={() => removeFleet(fleet.id)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding ? (
        <div className="add-form">
          <div className="form-row">
            <ColorPicker
              value={draft.color}
              onChange={(color) => setDraft({ ...draft, color })}
              label="new fleet colour"
            />
            <input
              type="text"
              placeholder="Fleet name (e.g. Blue Fleet)"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="inline-input"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && addFleet()}
            />
            <input
              type="time"
              value={draft.default_start_time}
              onChange={(e) => setDraft({ ...draft, default_start_time: e.target.value })}
              className="inline-input time-input"
            />
            <button className="btn btn-primary btn-sm" onClick={addFleet}>Add</button>
            <button className="btn btn-sm" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-sm" onClick={() => setAdding(true)}>+ Add Fleet</button>
      )}
    </section>
  );
}
